import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material'
import Header from '../components/Headers/Header'
import {
  getVendorEarnings,
  getVendorBalance,
  getVendorBankInfo,
  getVendorWithdrawRequests
} from '../apollo/queries'
import {
  updateBankAccount,
  requestVendorPayout
} from '../apollo/mutations'

const VENDOR_EARNINGS = gql`${getVendorEarnings}`
const VENDOR_BALANCE = gql`${getVendorBalance}`
const VENDOR_BANK_INFO = gql`${getVendorBankInfo}`
const VENDOR_WITHDRAW_REQUESTS = gql`${getVendorWithdrawRequests}`
const UPDATE_BANK_ACCOUNT = gql`${updateBankAccount}`
const REQUEST_PAYOUT = gql`${requestVendorPayout}`

const StatCard = ({ title, value, color }) => (
  <Paper sx={{ p: 3, textAlign: 'center', borderTop: `4px solid ${color}` }}>
    <Typography variant="body2" color="text.secondary">{title}</Typography>
    <Typography variant="h4" sx={{ mt: 1, fontWeight: 700, color }}>{value}</Typography>
  </Paper>
)

const Earnings = () => {
  const { t } = useTranslation()
  const restaurantId = localStorage.getItem('restaurantId')
  const [tab, setTab] = useState(0)
  const [bankDialog, setBankDialog] = useState(false)
  const [payoutDialog, setPayoutDialog] = useState(false)
  const [bankForm, setBankForm] = useState({ bankAccountName: '', bankRoutingNumber: '', bankAccountNumber: '' })
  const [payoutAmount, setPayoutAmount] = useState('')
  const [payoutNote, setPayoutNote] = useState('')
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  const { data: balanceData, loading: balanceLoading } = useQuery(VENDOR_BALANCE, {
    variables: { restaurant: restaurantId },
    skip: !restaurantId
  })

  const { data: earningsData, loading: earningsLoading } = useQuery(VENDOR_EARNINGS, {
    variables: { restaurant: restaurantId },
    skip: !restaurantId
  })

  const { data: bankData, refetch: refetchBank } = useQuery(VENDOR_BANK_INFO)

  const { data: withdrawData, refetch: refetchWithdraws } = useQuery(VENDOR_WITHDRAW_REQUESTS, {
    variables: { restaurant: restaurantId },
    skip: !restaurantId
  })

  const [updateBank, { loading: bankLoading }] = useMutation(UPDATE_BANK_ACCOUNT, {
    onCompleted: () => {
      setSuccess('Bank account updated successfully')
      setBankDialog(false)
      refetchBank()
      setTimeout(() => setSuccess(''), 5000)
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  const [requestPayoutMutation, { loading: payoutLoading }] = useMutation(REQUEST_PAYOUT, {
    onCompleted: () => {
      setSuccess('Payout request submitted successfully')
      setPayoutDialog(false)
      setPayoutAmount('')
      setPayoutNote('')
      refetchWithdraws()
      setTimeout(() => setSuccess(''), 5000)
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  const balance = balanceData?.vendorBalance
  const earnings = earningsData?.vendorEarnings || []
  const bank = bankData?.vendorBankInfo
  const withdrawRequests = withdrawData?.vendorWithdrawRequests || []

  const handleBankSubmit = () => {
    if (!bankForm.bankAccountName || !bankForm.bankRoutingNumber || !bankForm.bankAccountNumber) {
      setError('All bank fields are required')
      setTimeout(() => setError(''), 5000)
      return
    }
    updateBank({ variables: bankForm })
  }

  const handlePayoutSubmit = () => {
    const amt = parseFloat(payoutAmount)
    if (!amt || amt <= 0) {
      setError('Enter a valid amount')
      setTimeout(() => setError(''), 5000)
      return
    }
    requestPayoutMutation({
      variables: { restaurant: restaurantId, amount: amt, note: payoutNote || null }
    })
  }

  const availableBalance = balance
    ? (balance.pendingBalance - balance.pendingWithdrawals).toFixed(2)
    : '0.00'

  const statusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning'
      case 'PAID_OUT': case 'TRANSFERRED': return 'success'
      case 'REQUESTED': return 'info'
      case 'APPROVED': return 'primary'
      case 'CANCELLED': return 'error'
      default: return 'default'
    }
  }

  if (balanceLoading || earningsLoading) {
    return (
      <>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 20 }}>
          <CircularProgress />
        </Box>
      </>
    )
  }

  return (
    <>
      <Header />
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={600}>Earnings & Payouts</Typography>
          <Box>
            <Button
              variant="outlined"
              sx={{ mr: 1 }}
              onClick={() => setBankDialog(true)}
            >
              {bank?.hasBankAccount ? 'Update Bank Account' : 'Add Bank Account'}
            </Button>
            <Button
              variant="contained"
              disabled={!bank?.hasBankAccount || parseFloat(availableBalance) <= 0}
              onClick={() => setPayoutDialog(true)}
              sx={{ backgroundColor: '#5A9E6F', '&:hover': { backgroundColor: '#4a8a5f' } }}
            >
              Request Payout
            </Button>
          </Box>
        </Box>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Bank Account Info */}
        {bank?.hasBankAccount && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Bank account on file: <strong>{bank.bankAccountName}</strong> ending in <strong>****{bank.bankAccountLast4}</strong>
          </Alert>
        )}
        {!bank?.hasBankAccount && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No bank account on file. Add your bank account to request payouts.
          </Alert>
        )}

        {/* Balance Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Total Sales" value={`$${balance?.totalEarnings?.toFixed(2) || '0.00'}`} color="#1976d2" />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Commission Paid" value={`$${balance?.totalCommission?.toFixed(2) || '0.00'}`} color="#ed6c02" />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Your Earnings" value={`$${balance?.totalVendorAmount?.toFixed(2) || '0.00'}`} color="#2e7d32" />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Available Balance" value={`$${availableBalance}`} color="#5A9E6F" />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard title="Already Paid Out" value={`$${balance?.paidOutAmount?.toFixed(2) || '0.00'}`} color="#9e9e9e" />
          </Grid>
        </Grid>

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 2 }}>
          <Tab label={`Earnings (${earnings.length})`} />
          <Tab label={`Payout Requests (${withdrawRequests.length})`} />
        </Tabs>

        {/* Earnings Table */}
        {tab === 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Order #</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="right"><strong>Order Amount</strong></TableCell>
                  <TableCell align="right"><strong>Commission (%)</strong></TableCell>
                  <TableCell align="right"><strong>Commission</strong></TableCell>
                  <TableCell align="right"><strong>Your Earning</strong></TableCell>
                  <TableCell><strong>Payment</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {earnings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>No earnings yet</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  earnings.map((e) => (
                    <TableRow key={e._id} hover>
                      <TableCell>{e.orderNumber || `#${e.orderId}`}</TableCell>
                      <TableCell>{new Date(e.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">${e.orderAmount.toFixed(2)}</TableCell>
                      <TableCell align="right">{e.commissionRate}%</TableCell>
                      <TableCell align="right" sx={{ color: '#ed6c02' }}>-${e.commissionAmount.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 600 }}>${e.vendorAmount.toFixed(2)}</TableCell>
                      <TableCell>{e.paymentMethod}</TableCell>
                      <TableCell><Chip label={e.status} size="small" color={statusColor(e.status)} /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Payout Requests Table */}
        {tab === 1 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Request ID</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="right"><strong>Amount</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Note</strong></TableCell>
                  <TableCell><strong>Admin Note</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withdrawRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>No payout requests yet</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  withdrawRequests.map((r) => (
                    <TableRow key={r._id} hover>
                      <TableCell>#{r._id}</TableCell>
                      <TableCell>{new Date(r.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>${r.amount.toFixed(2)}</TableCell>
                      <TableCell><Chip label={r.status} size="small" color={statusColor(r.status)} /></TableCell>
                      <TableCell>{r.requestNote || '-'}</TableCell>
                      <TableCell>{r.adminNote || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Bank Account Dialog */}
        <Dialog open={bankDialog} onClose={() => setBankDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {bank?.hasBankAccount ? 'Update Bank Account' : 'Add Bank Account'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your bank details for receiving payouts. Your account number is stored securely.
            </Typography>
            <TextField
              fullWidth
              label="Account Holder Name"
              value={bankForm.bankAccountName}
              onChange={(e) => setBankForm({ ...bankForm, bankAccountName: e.target.value })}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              fullWidth
              label="Routing Number"
              value={bankForm.bankRoutingNumber}
              onChange={(e) => setBankForm({ ...bankForm, bankRoutingNumber: e.target.value })}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 9 }}
              helperText="9-digit routing number"
            />
            <TextField
              fullWidth
              label="Account Number"
              value={bankForm.bankAccountNumber}
              onChange={(e) => setBankForm({ ...bankForm, bankAccountNumber: e.target.value })}
              helperText="Your full bank account number"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBankDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleBankSubmit}
              disabled={bankLoading}
              sx={{ backgroundColor: '#5A9E6F', '&:hover': { backgroundColor: '#4a8a5f' } }}
            >
              {bankLoading ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payout Request Dialog */}
        <Dialog open={payoutDialog} onClose={() => setPayoutDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Request Payout</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Available balance: <strong>${availableBalance}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Payouts are processed weekly. You will receive funds in your bank account within 3-5 business days after approval.
            </Typography>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={payoutAmount}
              onChange={(e) => setPayoutAmount(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
              inputProps={{ min: 0, max: parseFloat(availableBalance), step: 0.01 }}
              helperText={`Max: $${availableBalance}`}
            />
            <TextField
              fullWidth
              label="Note (optional)"
              value={payoutNote}
              onChange={(e) => setPayoutNote(e.target.value)}
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPayoutDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handlePayoutSubmit}
              disabled={payoutLoading}
              sx={{ backgroundColor: '#5A9E6F', '&:hover': { backgroundColor: '#4a8a5f' } }}
            >
              {payoutLoading ? <CircularProgress size={20} /> : 'Submit Request'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  )
}

export default Earnings
